import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import type { TabConfig, QuestionConfig } from '../../types/api';

interface WizardPreviewProps {
  config: TabConfig[];
}

interface Answer {
  questionId: string;
  score: number;
  comment?: string;
}

interface TabProgress {
  tabId: string;
  completed: boolean;
  score: number;
  maxScore: number;
}

export default function WizardPreview({
  config,
}: WizardPreviewProps): ReactElement {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [tabProgress, setTabProgress] = useState<Record<string, TabProgress>>(
    {}
  );

  const currentTab = config[currentTabIndex];
  const totalTabs = config.length;

  // Inicializar progreso de tabs
  useEffect(() => {
    const progress: Record<string, TabProgress> = {};
    config.forEach((tab) => {
      const maxScore = tab.questions.length * 5; // 5 puntos máximo por pregunta
      progress[tab.id] = {
        tabId: tab.id,
        completed: false,
        score: 0,
        maxScore,
      };
    });
    setTabProgress(progress);
  }, [config]);

  // Actualizar progreso cuando cambien las respuestas
  useEffect(() => {
    const updatedProgress = { ...tabProgress };

    config.forEach((tab) => {
      const tabAnswers = tab.questions
        .map((q) => answers[q.id])
        .filter(Boolean);
      const score = tabAnswers.reduce(
        (sum, answer) => sum + (answer?.score || 0),
        0
      );
      const completed = tabAnswers.length === tab.questions.length;

      if (updatedProgress[tab.id]) {
        updatedProgress[tab.id] = {
          ...updatedProgress[tab.id],
          score,
          completed,
        };
      }
    });

    setTabProgress(updatedProgress);
  }, [answers, config, tabProgress]);

  const handleAnswerChange = (questionId: string, score: number): void => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        questionId,
        score,
        comment: prev[questionId]?.comment || '',
      },
    }));
  };

  const handleCommentChange = (questionId: string, comment: string): void => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        questionId,
        score: prev[questionId]?.score || 0,
        comment,
      },
    }));
  };

  const getScoreLabel = (score: number): { text: string; color: string } => {
    switch (score) {
      case 5:
        return { text: 'Excelente', color: 'text-green-600' };
      case 4:
        return { text: 'Bueno', color: 'text-blue-600' };
      case 3:
        return { text: 'Regular', color: 'text-yellow-600' };
      case 2:
        return { text: 'Deficiente', color: 'text-orange-600' };
      case 1:
        return { text: 'Muy Deficiente', color: 'text-red-600' };
      default:
        return { text: 'Sin calificar', color: 'text-gray-500' };
    }
  };

  const getTotalScore = (): {
    current: number;
    max: number;
    percentage: number;
  } => {
    const current = Object.values(tabProgress).reduce(
      (sum, tab) => sum + tab.score,
      0
    );
    const max = Object.values(tabProgress).reduce(
      (sum, tab) => sum + tab.maxScore,
      0
    );
    const percentage = max > 0 ? (current / max) * 100 : 0;

    return { current, max, percentage };
  };

  const getWeightedScore = (): number => {
    let weightedTotal = 0;
    let totalWeight = 0;

    config.forEach((tab) => {
      const progress = tabProgress[tab.id];
      if (progress && progress.maxScore > 0) {
        const tabPercentage = (progress.score / progress.maxScore) * 100;
        weightedTotal += tabPercentage * (tab.weightPct / 100);
        totalWeight += tab.weightPct;
      }
    });

    return totalWeight > 0 ? (weightedTotal / totalWeight) * 100 : 0;
  };

  const canGoNext = currentTabIndex < totalTabs - 1;
  const canGoPrev = currentTabIndex > 0;

  const totalScore = getTotalScore();
  const weightedScore = getWeightedScore();

  if (!currentTab) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">No hay configuración disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header con progreso */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Vista Previa del Wizard
            </h1>
            <p className="text-sm text-gray-500">
              Tab {currentTabIndex + 1} de {totalTabs}: {currentTab.title}
            </p>
          </div>

          <div className="text-right">
            <div className="text-sm text-gray-600">
              Puntaje Total:{' '}
              <span className="font-medium">
                {totalScore.current}/{totalScore.max}
              </span>
            </div>
            <div className="text-lg font-bold text-blue-600">
              {weightedScore.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Barra de progreso de tabs */}
        <div className="flex space-x-2">
          {config.map((tab, index) => {
            const progress = tabProgress[tab.id];
            const isActive = index === currentTabIndex;
            const isCompleted = progress?.completed || false;

            return (
              <div
                key={tab.id}
                className={`flex-1 h-2 rounded-full ${
                  isCompleted
                    ? 'bg-green-500'
                    : isActive
                    ? 'bg-blue-500'
                    : 'bg-gray-200'
                }`}
              />
            );
          })}
        </div>

        {/* Indicadores de tabs */}
        <div className="flex justify-between mt-2 text-xs">
          {config.map((tab, index) => {
            const progress = tabProgress[tab.id];
            const isCompleted = progress?.completed || false;

            return (
              <span
                key={tab.id}
                className={`flex items-center gap-1 ${
                  isCompleted ? 'text-green-600' : 'text-gray-500'
                }`}
              >
                {isCompleted && <CheckCircle className="w-3 h-3" />}
                {tab.title} ({tab.weightPct}%)
              </span>
            );
          })}
        </div>
      </div>

      {/* Contenido del tab actual */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {currentTab.title}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Peso: {currentTab.weightPct}% • {currentTab.questions.length}{' '}
                preguntas
              </p>
              {tabProgress[currentTab.id] && (
                <p className="text-sm text-blue-600 mt-1">
                  Progreso: {tabProgress[currentTab.id].score}/
                  {tabProgress[currentTab.id].maxScore} puntos
                </p>
              )}
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {currentTab.questions
                  .sort((a, b) => a.order - b.order)
                  .map((question, index) => {
                    const answer = answers[question.id];
                    const scoreLabel = getScoreLabel(answer?.score || 0);

                    return (
                      <div
                        key={question.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="mb-4">
                          <h3 className="font-medium text-gray-900 mb-2">
                            {index + 1}. {question.text}
                          </h3>
                          <p className="text-xs text-gray-500">
                            Máximo: {question.points || 5} puntos
                          </p>
                        </div>

                        {/* Escala de calificación */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Calificación:
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {[1, 2, 3, 4, 5].map((score) => (
                              <button
                                key={score}
                                onClick={() =>
                                  handleAnswerChange(question.id, score)
                                }
                                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                                  answer?.score === score
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {score}
                              </button>
                            ))}
                          </div>
                          {answer?.score && (
                            <p className={`text-sm mt-1 ${scoreLabel.color}`}>
                              {scoreLabel.text}
                            </p>
                          )}
                        </div>

                        {/* Comentarios */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Comentarios (opcional):
                          </label>
                          <textarea
                            value={answer?.comment || ''}
                            onChange={(e) =>
                              handleCommentChange(question.id, e.target.value)
                            }
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-950"
                            rows={2}
                            placeholder="Agregar observaciones..."
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentTabIndex((prev) => Math.max(0, prev - 1))}
            disabled={!canGoPrev}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </button>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Tab {currentTabIndex + 1} de {totalTabs}
            </p>
            {tabProgress[currentTab.id]?.completed && (
              <p className="text-xs text-green-600 flex items-center justify-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Completado
              </p>
            )}
          </div>

          <button
            onClick={() =>
              setCurrentTabIndex((prev) => Math.min(totalTabs - 1, prev + 1))
            }
            disabled={!canGoNext}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Resumen final */}
      {currentTabIndex === totalTabs - 1 && (
        <div className="bg-blue-50 border-t border-blue-200 px-6 py-4">
          <div className="text-center">
            <h3 className="font-semibold text-blue-900 mb-2">
              Resumen de Evaluación
            </h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-blue-700">Puntaje Total</p>
                <p className="font-bold text-blue-900">
                  {totalScore.current}/{totalScore.max}
                </p>
              </div>
              <div>
                <p className="text-blue-700">Porcentaje</p>
                <p className="font-bold text-blue-900">
                  {totalScore.percentage.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-blue-700">Puntaje Ponderado</p>
                <p className="font-bold text-blue-900">
                  {weightedScore.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
